<template>
  <!--
    Container de toasts globais (§3.5).
    Teleport para <body> para escapar do grid de App.vue.
    Montado uma única vez em App.vue.
  -->
  <Teleport to="body">
    <div class="p-toaster" aria-label="Notificações" aria-live="polite" aria-atomic="false">
      <TransitionGroup name="p-toast" tag="ul" class="p-toaster__lista">
        <li
          v-for="toast in fila"
          :key="toast.id"
          class="p-toast"
          :class="`p-toast--${toast.variante}`"
          :role="toast.variante === 'erro' ? 'alert' : 'status'"
          @mouseenter="pausar(toast.id)"
          @mouseleave="retomar(toast.id)"
        >
          <IconeSVG
            class="p-toast__icone"
            :icone="icones[toast.variante]"
            cor="currentColor"
            width="18px"
            height="18px"
            aria-hidden="true"
          />
          <span class="p-toast__mensagem">{{ toast.mensagem }}</span>
          <button
            class="p-toast__fechar"
            type="button"
            aria-label="Fechar notificação"
            @click="fechar(toast.id)"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </li>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script>
import IconeSVG from '@/common/IconeSVG.vue'
import {
  filaToast,
  removerToast,
  pausarToast,
  retomarToast,
} from '@/common/toast/ServicoToast'

/**
 * Container global de toasts (§3.5).
 * Deve ser montado UMA vez, em App.vue.
 * Não recebe props — toda interação acontece via ServicoToast / $toast.
 */

const ICONES_POR_VARIANTE = {
  sucesso: 'certo',
  alerta: 'alerta',
  erro: 'errado',
  info: 'informativo',
}

export default {
  name: 'PToaster',

  components: { IconeSVG },

  computed: {
    fila() {
      return filaToast
    },
    icones() {
      return ICONES_POR_VARIANTE
    },
  },

  methods: {
    fechar(id) {
      removerToast(id)
    },
    pausar(id) {
      pausarToast(id)
    },
    retomar(id) {
      retomarToast(id)
    },
  },
}
</script>

<style lang="scss" scoped>
/* Contêiner fixo no canto inferior direito */
.p-toaster {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  pointer-events: none; /* não bloqueia cliques na área vazia */
}

.p-toaster__lista {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Toast individual */
.p-toast {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  min-width: 18rem;
  max-width: 26rem;
  padding: 0.75rem 0.875rem;
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, var(--cor-toast) 30%, transparent);
  border-left: 4px solid var(--cor-toast);
  background-color: color-mix(in srgb, var(--cor-toast) 8%, var(--superficie-cartao, #fff));
  box-shadow: 0 2px 8px color-mix(in srgb, var(--cor-toast) 16%, transparent);
  color: var(--cor-toast);
  pointer-events: all; /* restaura cliques para o toast em si */
  cursor: default;
}

.p-toast__icone {
  flex: 0 0 auto;
  margin-top: 1px; /* alinhamento óptico com a primeira linha de texto */
}

.p-toast__mensagem {
  flex: 1 1 auto;
  font-family: var(--fonte-corpo);
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--texto-forte);
  word-break: break-word;
}

.p-toast__fechar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--texto-suave);
  font-size: 1.125rem;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: color-mix(in srgb, var(--cor-toast) 12%, transparent);
    color: var(--cor-toast);
  }

  &:focus-visible {
    outline: 2px solid var(--borda-foco);
    outline-offset: 2px;
  }
}

/* Cor semântica por variante (§1 — apenas tokens semânticos) */
.p-toast--sucesso {
  --cor-toast: var(--status-sucesso);
}

.p-toast--alerta {
  --cor-toast: var(--status-alerta);
}

.p-toast--erro {
  --cor-toast: var(--status-erro);
}

.p-toast--info {
  --cor-toast: var(--status-info);
}

/* Animações de entrada / saída */
.p-toast-enter-active,
.p-toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.p-toast-enter-from {
  opacity: 0;
  transform: translateY(0.5rem);
}

.p-toast-leave-to {
  opacity: 0;
  transform: translateX(0.5rem);
}

.p-toast-move {
  transition: transform 0.22s ease;
}
</style>
